import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Plus, Camera, Image as ImageIcon, Loader2, LogIn, LogOut as LogOutIcon } from 'lucide-react';
import { safeFetch, extractError } from '../../lib/safeFetch';
import { compressImage } from '../../lib/imageCompression';
import { getPreciseLocation } from '../../lib/preciseLocation';
import LocationVerifyingModal from './LocationVerifyingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return null; }
}

function durationMinutes(p) {
  const o = p.out?.time, i = p.in?.time;
  if (!o || !i) return null;
  try {
    const d = (new Date(i) - new Date(o)) / 60000;
    return d >= 0 ? Math.round(d) : null;
  } catch { return null; }
}

export default function PermissionTracker() {
  const [permissions, setPermissions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoGps, setPhotoGps] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/permissions/my`, { headers: { 'Authorization': `Bearer ${token}` } });
    const d = await r.json();
    setPermissions(d);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setPhotoGps({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      });
    }
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      setPhoto(compressed);
    } finally {
      setCompressing(false);
    }
  };

  // Open the system photo picker (gallery / Google Photos / Files). On native
  // Capacitor Android, tapping a `<input type="file">` without a `capture`
  // attribute triggers the platform's media chooser, which prompts the user
  // for READ_MEDIA_IMAGES (Android 13+) / READ_EXTERNAL_STORAGE (≤12) the
  // first time — no separate permission code needed on our side.
  const openGallery = () => {
    if (galleryRef.current) {
      galleryRef.current.value = '';   // reset so picking the same file again still fires onChange
      galleryRef.current.click();
    }
  };
  const openCamera = () => {
    if (fileRef.current) {
      fileRef.current.value = '';
      fileRef.current.click();
    }
  };

  const create = async (e) => {
    e.preventDefault();
    // Guard against double-submit: GPS + safeFetch retries can take 5-10s on
    // flaky cellular and impatient users tap the button repeatedly. Each tap
    // would otherwise fire a separate /api/permissions POST and create
    // duplicate rows (May 29, 2026 bug — 6× "school admisionn" appeared from
    // one user intent).
    if (submitting) return;
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      const result = await safeFetch(`${API_URL}/api/permissions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requested_hours: parseFloat(hours),
          reason,
          photo_base64: photo,
          photo_gps_lat: photoGps?.latitude,
          photo_gps_lon: photoGps?.longitude
        })
      });
      if (result.ok) {
        if (result.data?.duplicate) {
          alert('You just submitted the same request — saved once. No duplicate created.');
        } else {
          alert('Permission requested');
        }
        setShowCreate(false);
        setHours(''); setReason(''); setPhoto(null); setPhotoGps(null);
        fetchAll();
      } else {
        alert(extractError(result, 'Failed to request permission'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const punch = async (id, which) => {
    setVerifying({ action: which === 'in' ? 'Permission — IN' : 'Permission — OUT', taken: 0, lastAccuracy: null });
    let loc;
    try {
      loc = await getPreciseLocation({
        onProgress: (taken, _t, last) =>
          setVerifying((v) => v && { ...v, taken, lastAccuracy: last?.accuracy ?? v.lastAccuracy }),
      });
    } catch (e) {
      alert(e?.message || 'Location required'); setVerifying(null); return;
    }
    setVerifying(null);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/permissions/${which}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ permission_id: id, ...loc })
    });
    if (result.ok) { alert(which === 'in' ? 'Returned' : 'Started'); fetchAll(); }
    else { alert(extractError(result, 'Permission action failed')); }
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border" data-testid="permission-tracker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <ShieldCheck className="text-purple-500" size={20} />
          <h3 className="font-semibold">Permissions</h3>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} className="text-sm text-purple-600 flex items-center space-x-1" data-testid="permission-add-btn">
          <Plus size={14} /><span>Request</span>
        </button>
      </div>

      {showCreate && (
        <form onSubmit={create} className="space-y-2 mb-4 p-3 bg-purple-50 rounded-lg">
          <input
            type="number" step="0.5" placeholder="Hours requested"
            value={hours} onChange={(e) => setHours(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm" required
            data-testid="permission-hours-input"
          />
          <input
            placeholder="Reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm"
            data-testid="permission-reason-input"
          />
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" data-testid="permission-photo-input" />
          <input ref={galleryRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="permission-gallery-input" />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" disabled={compressing} onClick={openCamera} className="px-3 py-2 border-2 border-dashed border-purple-300 rounded text-sm flex items-center justify-center space-x-1 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="permission-photo-btn">
              <Camera size={14} /><span>{photo ? 'Re-take' : 'Camera'}</span>
            </button>
            <button type="button" disabled={compressing} onClick={openGallery} className="px-3 py-2 border-2 border-dashed border-purple-300 rounded text-sm flex items-center justify-center space-x-1 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="permission-gallery-btn">
              <ImageIcon size={14} /><span>{photo ? 'Re-pick' : 'Gallery'}</span>
            </button>
          </div>
          {compressing && (
            <p className="text-xs text-purple-700 font-medium text-center flex items-center justify-center gap-1.5" data-testid="permission-compressing">
              <Loader2 size={12} className="animate-spin" /> Compressing…
            </p>
          )}
          {!compressing && photo && (
            <p className="text-xs text-emerald-700 font-medium text-center" data-testid="permission-photo-status">
              ✓ Slip attached
            </p>
          )}
          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-2 bg-purple-600 disabled:bg-purple-300 disabled:cursor-not-allowed text-white rounded text-sm flex items-center gap-1.5"
              data-testid="permission-submit-btn"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
            <button type="button" disabled={submitting} onClick={() => setShowCreate(false)} className="px-3 py-2 bg-gray-200 disabled:opacity-50 rounded text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {permissions.length === 0 && <p className="text-xs text-gray-500">No permissions yet</p>}
        {permissions.map(p => (
          <div key={p.id} className="border rounded-lg p-3 text-sm" data-testid={`permission-row-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium">{p.requested_hours}h • {p.date}</p>
                <p className="text-xs text-gray-500">{p.reason}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${p.status === 'completed' ? 'bg-green-100 text-green-700' : p.status === 'ongoing' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                {p.status}
              </span>
            </div>
            {p.status === 'requested' && (
              <button onClick={() => punch(p.id, 'out')} className="mt-2 px-3 py-1 bg-orange-500 text-white rounded text-xs flex items-center space-x-1">
                <LogOutIcon size={12} /><span>Permission Out</span>
              </button>
            )}
            {p.status === 'ongoing' && (
              <>
                <p className="mt-1 text-xs text-gray-600">Out at {fmtTime(p.out?.time) || '—'} · ongoing</p>
                <button onClick={() => punch(p.id, 'in')} className="mt-2 px-3 py-1 bg-green-600 text-white rounded text-xs flex items-center space-x-1">
                  <LogIn size={12} /><span>Permission In</span>
                </button>
              </>
            )}
            {p.status === 'completed' && (() => {
              const m = durationMinutes(p);
              return (
                <p className="mt-1 text-xs text-gray-700" data-testid={`permission-duration-${p.id}`}>
                  {fmtTime(p.out?.time) || '—'} → {fmtTime(p.in?.time) || '—'}
                  {m != null && <strong className="ml-2 text-emerald-700">{m} min</strong>}
                </p>
              );
            })()}
          </div>
        ))}
      </div>
      {verifying && (
        <LocationVerifyingModal action={verifying.action} taken={verifying.taken} lastAccuracy={verifying.lastAccuracy} />
      )}
    </div>
  );
}
